"use client";

import React, { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import type { Thread, FolderWithChildren } from "@/types/chat";
import type { Chatbot, ChatbotFolderWithChildren } from "@/types/chatbot";
import { FolderTree } from "./FolderTree";
import NewThreadModal from "./NewThreadModal";
import { ChatbotFolderTree, NewChatbotModal, ChatbotSettingsPanel } from "@/app/chatbots/components";
import type { CreateChatbotInput } from "@/types/chatbot";
import type { ChatbotFileConfig } from "@/types/chatbotFile";
type SidebarProps = {
  userEmail: string | null | undefined;
  threads: Thread[];
  selectedThreadId: string | null;
  onSelectThread: (id: string) => void;
  onNewThread: (name: string, folderId: string | null, chatbotId?: string | null) => void;
  onDeleteThread: (id: string) => Promise<void>;
  onUpdateThreadTitle: (id: string, newTitle: string) => Promise<void>;
  onSignOut: () => void;
  canCreateThread?: boolean;
  threadLimitReached?: boolean;
  maxThreads?: number;
  userTier?: "trial" | "pro" | "expired" | "pending";
  // Folder props
  folderTree?: FolderWithChildren[];
  onCreateFolder?: (name: string, parentId?: string | null) => Promise<any>;
  onUpdateFolder?: (id: string, updates: {
    name?: string;
    color?: string;
    is_collapsed?: boolean;
  }) => Promise<void>;
  onDeleteFolder?: (id: string) => Promise<void>;
  onMoveFolder?: (folderId: string, newParentId: string | null) => Promise<void>;
  onMoveThread?: (threadId: string, folderId: string | null) => Promise<void>;
  onBulkMoveThreads?: (threadIds: string[], folderId: string | null) => Promise<void>;
  onToggleFolderCollapse?: (folderId: string) => Promise<void>;
  // Context panel props
  threadContextIds?: Set<string>;
  onAddThreadToContext?: (threadId: string, threadTitle: string) => void;
  // Thread info props
  onShowThreadInfo?: (threadId: string) => void;
  // Chatbot props
  chatbots?: Chatbot[];
  chatbotFolderTree?: ChatbotFolderWithChildren[];
  selectedChatbotId?: string | null;
  onSelectChatbot?: (id: string) => void;
  onCreateChatbot?: (input: CreateChatbotInput) => Promise<void>;
  onEditChatbot?: (id: string) => void;
  onDuplicateChatbot?: (id: string) => Promise<void>;
  onDeleteChatbot?: (id: string) => Promise<void>;
  onRenameChatbot?: (id: string, newName: string) => Promise<void>;
  onUpdateChatbotConfig?: (id: string, config: ChatbotFileConfig) => Promise<void>;
  chatbotDefaultFolderId?: string | null;
  currentChatbotConfig?: any;
  // Controlled chatbot editing state from parent
  editingChatbotId?: string | null;
  onCloseChatbotSettings?: () => void;
  /** Called when draft config changes for real-time preview */
  onDraftConfigChange?: (config: ChatbotFileConfig | null) => void;
  // Chatbot folder props
  onCreateChatbotFolder?: (input: {
    name: string;
    parent_id?: string | null;
  }) => Promise<any>;
  onUpdateChatbotFolder?: (id: string, updates: {
    name?: string;
    color?: string;
    is_collapsed?: boolean;
  }) => Promise<boolean>;
  onDeleteChatbotFolder?: (id: string) => Promise<boolean>;
  onMoveChatbotFolder?: (folderId: string, newParentId: string | null) => Promise<boolean>;
  onMoveChatbot?: (chatbotId: string, folderId: string | null) => Promise<boolean>;
  onToggleChatbotFolderCollapse?: (folderId: string) => Promise<boolean>;
};
export default function Sidebar({
  userEmail,
  threads,
  selectedThreadId,
  onSelectThread,
  onNewThread,
  onDeleteThread,
  onUpdateThreadTitle,
  onSignOut,
  canCreateThread = true,
  threadLimitReached = false,
  maxThreads = 5,
  userTier = "trial",
  folderTree = [],
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
  onMoveFolder,
  onMoveThread,
  onBulkMoveThreads,
  onToggleFolderCollapse,
  threadContextIds,
  onAddThreadToContext,
  onShowThreadInfo,
  // Chatbot props
  chatbots = [],
  chatbotFolderTree = [],
  selectedChatbotId,
  onSelectChatbot,
  onCreateChatbot,
  onEditChatbot,
  onDuplicateChatbot,
  onDeleteChatbot,
  onRenameChatbot,
  onUpdateChatbotConfig,
  chatbotDefaultFolderId,
  currentChatbotConfig,
  editingChatbotId,
  onCloseChatbotSettings,
  onDraftConfigChange,
  // Chatbot folder props
  onCreateChatbotFolder,
  onUpdateChatbotFolder,
  onDeleteChatbotFolder,
  onMoveChatbotFolder,
  onMoveChatbot,
  onToggleChatbotFolderCollapse
}: SidebarProps) {
  // Check if folder features are enabled (all folder props provided)
  const hasFolderFeatures = !!(onCreateFolder && onUpdateFolder && onDeleteFolder && onMoveFolder && onMoveThread && onBulkMoveThreads && onToggleFolderCollapse);

  // Check if chatbot folder features are enabled
  const hasChatbotFolderFeatures = !!(onCreateChatbotFolder && onUpdateChatbotFolder && onDeleteChatbotFolder && onMoveChatbotFolder && onMoveChatbot && onToggleChatbotFolderCollapse);

  // Get root-level threads (threads without a folder)
  const rootThreads = threads.filter(t => !t.folder_id);

  // Get root-level chatbots (chatbots without a folder)
  const rootChatbots = chatbots.filter(c => !c.folder_id);
  const router = useRouter();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [isNewThreadModalOpen, setIsNewThreadModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const [isNewChatbotModalOpen, setIsNewChatbotModalOpen] = useState(false);
  const [isChatbotsCollapsed, setIsChatbotsCollapsed] = useState(false);
  // Pending chatbot for new thread creation
  const [pendingChatbotId, setPendingChatbotId] = useState<string | null>(null);
  const [pendingChatbotName, setPendingChatbotName] = useState<string | null>(null);

  // Resizable chatbots section
  const [chatbotsHeight, setChatbotsHeight] = useState(200);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (editingThreadId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingThreadId]);

  // Handle resize drag
  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - resizeStartY.current;
      const newHeight = Math.max(100, Math.min(400, resizeStartHeight.current + deltaY));
      setChatbotsHeight(newHeight);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);
  const handleResizeStart = (e_0: React.MouseEvent) => {
    e_0.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e_0.clientY;
    resizeStartHeight.current = chatbotsHeight;
  };

  // Find the default folder ID
  const findDefaultFolderId = (folders: FolderWithChildren[]): string | null => {
    for (const folder of folders) {
      if (folder.is_default) return folder.id;
      if (folder.children) {
        const found = findDefaultFolderId(folder.children);
        if (found) return found;
      }
    }
    return null;
  };
  const defaultFolderId = findDefaultFolderId(folderTree);
  const handleOpenNewThreadModal = () => {
    if (!canCreateThread) return;
    // Clear any pending chatbot when opening from the + button
    setPendingChatbotId(null);
    setPendingChatbotName(null);
    setIsNewThreadModalOpen(true);
  };
  const handleStartChatbotThread = (chatbotId: string, chatbotName: string) => {
    if (!canCreateThread) return;
    // Set the pending chatbot before opening the modal
    setPendingChatbotId(chatbotId);
    setPendingChatbotName(chatbotName);
    setIsNewThreadModalOpen(true);
  };
  const handleCloseNewThreadModal = () => {
    setIsNewThreadModalOpen(false);
    // Clear pending chatbot when modal closes
    setPendingChatbotId(null);
    setPendingChatbotName(null);
  };
  const handleCreateThread = (name: string, folderId: string | null, chatbotId_0?: string | null) => {
    onNewThread(name, folderId, chatbotId_0);
  };
  const handleDelete = (e_1: React.MouseEvent, threadId: string, threadTitle: string) => {
    e_1.stopPropagation(); // Prevent selecting the thread when clicking delete

    const confirmed = window.confirm(`Are you sure you want to delete "${threadTitle || 'New thread'}"?\n\nThis will permanently delete the thread and all its messages.`);
    if (confirmed) {
      onDeleteThread(threadId);
    }
  };
  const startEditing = (e_2: React.MouseEvent, threadId_0: string, currentTitle: string) => {
    e_2.stopPropagation(); // Prevent selecting the thread when clicking edit
    setEditingThreadId(threadId_0);
    setEditedTitle(currentTitle || "New thread");
  };
  const cancelEditing = () => {
    setEditingThreadId(null);
    setEditedTitle("");
  };
  const saveTitle = async (threadId_1: string) => {
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      cancelEditing();
      return;
    }
    await onUpdateThreadTitle(threadId_1, trimmedTitle);
    cancelEditing();
  };
  const handleKeyDown = (e_3: React.KeyboardEvent, threadId_2: string) => {
    if (e_3.key === "Enter") {
      e_3.preventDefault();
      saveTitle(threadId_2);
    } else if (e_3.key === "Escape") {
      cancelEditing();
    }
  };

  // Find the chatbot being edited - handle special __default__ case
  const isEditingDefaultChatbot = editingChatbotId === '__default__';
  const editingChatbot: Chatbot | null = editingChatbotId ? isEditingDefaultChatbot ? {
    // Virtual default chatbot for editing
    id: '__default__',
    name: 'default',
    description: null,
    user_id: '',
    // Will be set when creating
    folder_id: null,
    position: 0,
    config: {
      model: {
        provider: 'openai',
        model_name: 'gpt-4o'
      },
      system_prompt: '',
      features: {}
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  } : chatbots.find(c_0 => c_0.id === editingChatbotId) ?? null : null;
  const handleOpenChatbotSettingsLocal = (id: string) => {
    const chatbot = chatbots.find(c_1 => c_1.id === id);
    console.log('[Sidebar] Opening chatbot settings panel:', chatbot?.name, id);
    // Call parent handler to open settings panel (controlled from page.tsx)
    onEditChatbot?.(id);
  };
  const handleCloseChatbotSettingsLocal = () => {
    console.log('[Sidebar] Closing chatbot settings panel');
    // Clear draft config for real-time preview
    onDraftConfigChange?.(null);
    // Call parent handler to close settings panel (controlled from page.tsx)
    onCloseChatbotSettings?.();
  };
  const handleSaveChatbotSettings = async (config: ChatbotFileConfig) => {
    console.log('[Sidebar] Saving chatbot settings:', editingChatbotId);

    // Special handling for default chatbot - create new instead of update
    if (isEditingDefaultChatbot && onCreateChatbot) {
      // Prompt for name
      const name_0 = window.prompt('Enter a name for your new chatbot:', 'My Chatbot');
      if (!name_0 || !name_0.trim()) {
        // User cancelled - don't save
        return;
      }
      try {
        await onCreateChatbot({
          name: name_0.trim(),
          folder_id: chatbotDefaultFolderId || undefined,
          config
        });
        console.log('[Sidebar] New chatbot created from default settings');
      } catch (error) {
        console.error('[Sidebar] Failed to create chatbot:', error);
        throw error;
      }
    } else if (editingChatbotId && onUpdateChatbotConfig) {
      try {
        await onUpdateChatbotConfig(editingChatbotId, config);
        console.log('[Sidebar] Chatbot settings saved successfully');
      } catch (error_0) {
        console.error('[Sidebar] Failed to save chatbot settings:', error_0);
        throw error_0; // Re-throw so panel can handle it
      }
    }
    // Clear draft config for real-time preview
    onDraftConfigChange?.(null);
    // Close the panel via parent handler (controlled from page.tsx)
    onCloseChatbotSettings?.();
  };
  return <div className="flex h-full flex-col bg-white/40 border-r border-white/40 text-foreground backdrop-blur-md">
      {/* Top: app + user info + new thread */}
      <div className="border-b border-white/30 px-3 py-4">
        {/* Logo */}
        <div className="mb-3 px-1">
          <img src="/logo.png" alt="aiuiw" className="h-8 w-auto brightness-90" />
        </div>

        {/* User Menu Button */}
        <div className="relative mt-1" ref={menuRef}>
          <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-white/40 transition-colors">
            <div className="flex items-center gap-2 truncate">
              <span className="truncate">{userEmail ?? "Signed in"}</span>
              {userTier === "pro" && <span className="inline-flex items-center rounded-full bg-purple-500/10 px-2 py-0.5 text-xs font-medium text-purple-600 ring-1 ring-inset ring-purple-500/20">
                  Pro
                </span>}
              {userTier === "trial" && <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 ring-1 ring-inset ring-amber-500/20">
                  Trial
                </span>}
              {userTier === "expired" && <span className="inline-flex items-center rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-500/20">
                  Expired
                </span>}
            </div>
            <svg className={`h-4 w-4 flex-shrink-0 transition-transform ${isUserMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isUserMenuOpen && <div className="absolute left-0 right-0 mt-1 rounded-md border border-white/50 bg-white/80 backdrop-blur-md shadow-lg z-10">
              <button onClick={() => {
            setIsUserMenuOpen(false);
            router.push("/settings");
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-white/60 transition-colors rounded-t-md">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>
              <button onClick={() => {
            setIsUserMenuOpen(false);
            onSignOut();
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-white/60 transition-colors rounded-b-md">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>}
        </div>

        {/* New Thread and Import buttons */}
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={handleOpenNewThreadModal} disabled={!canCreateThread} className={`flex-1 rounded-full px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sky/50 focus:ring-offset-2 focus:ring-offset-white ${canCreateThread ? "rainbow-gradient border border-foreground/30" : "border border-white/30 bg-white/20 text-foreground-secondary/50 cursor-not-allowed"}`} title={!canCreateThread ? "Thread limit reached. Subscribe to Pro for unlimited threads." : "Create a new .thread file"}>
            + New .thread
          </button>
        </div>

        {/* Thread limit warning - only shown for expired tier */}
        {threadLimitReached && userTier === "expired" && <div className="mt-2 rounded-md bg-red-500/10 border border-red-500/20 px-2 py-2 text-xs text-red-400">
            <div className="font-semibold mb-1">Trial Expired</div>
            <div className="text-red-400/80">
              Your trial has ended. Subscribe to Pro to continue creating threads and using AI features.{" "}
              <button onClick={() => router.push("/settings")} className="underline hover:text-red-300">
                Subscribe Now
              </button>
            </div>
          </div>}
      </div>

      {/* Navigation: Chatbot & Canvas */}
      <div className="border-b border-white/30 px-3 py-3">
        <div className="space-y-2">
          <button type="button" onClick={() => router.push("/dashboard")} className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-white/40 transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Chatbot
          </button>
          <button type="button" onClick={() => router.push("/canvas")} className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-white/40 transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
            </svg>
            Canvas
          </button>
          <button type="button" onClick={() => router.push("/exchange")} className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-white/40 transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Exchange
          </button>
        </div>
      </div>

      {/* Chatbots Section - above Threads */}
      {onCreateChatbot && <div className="flex flex-col border-b border-white/30" style={{
      height: isChatbotsCollapsed ? 'auto' : chatbotsHeight
    }}>
          {/* Section Header */}
          <div className="flex items-center justify-between px-3 py-2 flex-shrink-0">
            <button type="button" onClick={() => setIsChatbotsCollapsed(!isChatbotsCollapsed)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-foreground-secondary/70 hover:text-foreground">
              <svg className={`h-3 w-3 transition-transform ${isChatbotsCollapsed ? "-rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Chatbots
            </button>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setIsNewChatbotModalOpen(true)} className="rounded p-1 text-foreground-secondary/70 hover:bg-white/40 hover:text-foreground" title="New chatbot">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chatbot Folder Tree */}
          {!isChatbotsCollapsed && hasChatbotFolderFeatures && <div className="flex-1 overflow-y-auto px-2 pb-2">
              <ChatbotFolderTree folders={chatbotFolderTree} chatbots={rootChatbots} selectedChatbotId={selectedChatbotId ?? null} onSelectChatbot={onSelectChatbot || (() => {})} onDeleteChatbot={onDeleteChatbot ? id_0 => onDeleteChatbot(id_0) : Promise.resolve} onRenameChatbot={onRenameChatbot ? (id_1, name_1) => onRenameChatbot(id_1, name_1) : async () => {}} onCreateFolder={onCreateChatbotFolder} onUpdateFolder={onUpdateChatbotFolder} onDeleteFolder={onDeleteChatbotFolder} onMoveFolder={onMoveChatbotFolder} onMoveChatbot={onMoveChatbot} onToggleFolderCollapse={onToggleChatbotFolderCollapse} onStartChatbotThread={handleStartChatbotThread} onEditChatbot={handleOpenChatbotSettingsLocal} onDuplicateChatbot={onDuplicateChatbot} />
            </div>}
        </div>}

      {/* Resizable Divider */}
      {onCreateChatbot && !isChatbotsCollapsed && <div className={`h-1 flex-shrink-0 cursor-ns-resize bg-white/30 hover:bg-secondary-sky/50 transition-colors ${isResizing ? "bg-secondary-sky/50" : ""}`} onMouseDown={handleResizeStart} title="Drag to resize" />}

      {/* Threads section */}
      <div className="flex-1 overflow-y-auto px-2 py-3 min-h-0">
        <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-foreground-secondary/70">
          Threads
        </div>

        {/* Use FolderTree when folder features are enabled */}
        {hasFolderFeatures ? <FolderTree folders={folderTree} threads={rootThreads} selectedThreadId={selectedThreadId} onSelectThread={onSelectThread} onDeleteThread={onDeleteThread} onUpdateThreadTitle={onUpdateThreadTitle} onCreateFolder={onCreateFolder} onUpdateFolder={onUpdateFolder} onDeleteFolder={onDeleteFolder} onMoveFolder={onMoveFolder} onMoveThread={onMoveThread} onBulkMoveThreads={onBulkMoveThreads} onToggleFolderCollapse={onToggleFolderCollapse} threadContextIds={threadContextIds} onAddThreadToContext={onAddThreadToContext} onShowThreadInfo={onShowThreadInfo} /> : <>
            {/* Legacy thread list (fallback when folders not enabled) */}
            {threads.length === 0 && <div className="px-2 py-1 text-xs text-foreground-secondary/60">
                No threads yet. Create one to get started.
              </div>}
            <ul className="space-y-1">
              {threads.map(thread => {
            const isActive = thread.id === selectedThreadId;
            const isEditing = editingThreadId === thread.id;
            return <li key={thread.id} className="group relative">
                    {isEditing ? <div className="flex items-center gap-1 px-2 py-1.5">
                        <input ref={editInputRef} type="text" value={editedTitle} onChange={e_4 => setEditedTitle(e_4.target.value)} onKeyDown={e_5 => handleKeyDown(e_5, thread.id)} onBlur={() => saveTitle(thread.id)} className="flex-1 rounded border border-white/40 bg-white/60 px-2 py-1 text-sm text-foreground focus:border-secondary-sky focus:outline-none focus:ring-1 focus:ring-secondary-sky" />
                      </div> : <>
                        <button type="button" onClick={() => onSelectThread(thread.id)} className={`w-full rounded-md px-2 py-1.5 pr-16 text-left text-sm transition-colors ${isActive ? "bg-white/60 text-foreground shadow-sm font-medium" : "text-foreground-secondary/80 hover:bg-white/30"}`}>
                          <span className="block truncate">
                            {thread.title || "New thread"}
                          </span>
                        </button>
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button type="button" onClick={e_6 => startEditing(e_6, thread.id, thread.title || "New thread")} className="rounded p-1 hover:bg-blue-600/20 text-foreground-secondary/50 hover:text-blue-600" title="Edit thread name">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                          <button type="button" onClick={e_7 => handleDelete(e_7, thread.id, thread.title || "New thread")} className="rounded p-1 hover:bg-red-600/20 text-foreground-secondary/50 hover:text-red-600" title="Delete thread">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </>}
                  </li>;
          })}
            </ul>
          </>}
      </div>

      {/* New Thread Modal */}
      <NewThreadModal isOpen={isNewThreadModalOpen} onClose={handleCloseNewThreadModal} onCreateThread={handleCreateThread} folderTree={folderTree} defaultFolderId={defaultFolderId} chatbots={chatbots} chatbotId={pendingChatbotId} chatbotName={pendingChatbotName} />

      {/* New Chatbot Modal */}
      {onCreateChatbot && <NewChatbotModal isOpen={isNewChatbotModalOpen} onClose={() => setIsNewChatbotModalOpen(false)} onCreate={onCreateChatbot} folderTree={chatbotFolderTree} defaultFolderId={chatbotDefaultFolderId} currentConfig={currentChatbotConfig} />}

      {/* Chatbot Settings Panel */}
      {editingChatbot && <ChatbotSettingsPanel chatbot={editingChatbot} isOpen={editingChatbotId !== null} onClose={handleCloseChatbotSettingsLocal} onSave={handleSaveChatbotSettings} onDraftChange={onDraftConfigChange} />}
    </div>;
}